// Pricing logic - domain layer
// Price is ALWAYS calculated here, never stored (see docs/crochet/specs/2026-07-06-S04-precificacao.md)
//
// materiais        Σ (quantity × unit_price_snapshot)
// mão de obra      Σ horas(time_entries) × valor da hora
// custos extras    valor fixo opcional
// ──────────────────────────────────────────────────
// custo total
// × (1 + mark-up %)                       → preço base
// ÷ (1 − taxa de venda %)                 → preço final ("por fora": divide, não multiplica)

import type { Project, ProjectSupply, TimeEntry, Profile } from '../db/schema';

export interface PricingInput {
  project: Pick<Project, 'hourlyRateOverride' | 'markupPctOverride' | 'extraCosts' | 'saleFeePct'>;
  profile: Pick<Profile, 'hourlyRate' | 'defaultMarkupPct'>;
  projectSupplies: Array<Pick<ProjectSupply, 'quantity' | 'unitPriceSnapshot'>>;
  timeEntries: Array<Pick<TimeEntry, 'startedAt' | 'endedAt'>>;
}

export interface PriceBreakdown {
  materialsCost: number;
  laborHours: number;
  hourlyRate: number;
  laborCost: number;
  extraCosts: number;
  totalCost: number;
  markupPct: number;
  markupAmount: number;
  basePrice: number;
  saleFeePct: number;
  finalPrice: number;
  currency: string;
}

const DEFAULT_CURRENCY = 'BRL';

// Rounding happens only when a value is placed on display (half-up, 2 casas).
// Every intermediate step below chains full, unrounded precision.
function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function calculateMaterialsCost(projectSupplies: PricingInput['projectSupplies']): number {
  return projectSupplies.reduce((sum, ps) => sum + ps.quantity * ps.unitPriceSnapshot, 0);
}

export function calculateLaborSeconds(timeEntries: PricingInput['timeEntries']): number {
  return timeEntries.reduce((sum, te) => {
    if (!te.endedAt) return sum; // ongoing session not counted
    const seconds = (te.endedAt.getTime() - te.startedAt.getTime()) / 1000;
    return sum + Math.max(0, seconds);
  }, 0);
}

export function calculateLaborCost(timeEntries: PricingInput['timeEntries'], hourlyRate: number): number {
  const hours = calculateLaborSeconds(timeEntries) / 3600;
  return hours * hourlyRate;
}

export function calculatePrice(input: PricingInput): PriceBreakdown {
  const hourlyRate = input.project.hourlyRateOverride ?? input.profile.hourlyRate;
  const markupPct = input.project.markupPctOverride ?? input.profile.defaultMarkupPct;
  const extraCosts = input.project.extraCosts;
  const saleFeePct = input.project.saleFeePct;

  const materialsCost = calculateMaterialsCost(input.projectSupplies);
  const laborHours = calculateLaborSeconds(input.timeEntries) / 3600;
  const laborCost = calculateLaborCost(input.timeEntries, hourlyRate);

  const totalCost = materialsCost + laborCost + extraCosts;
  const markupAmount = totalCost * (markupPct / 100);
  const basePrice = totalCost + markupAmount;
  const finalPrice = basePrice / (1 - saleFeePct / 100);

  return {
    materialsCost: round2(materialsCost),
    laborHours,
    hourlyRate,
    laborCost: round2(laborCost),
    extraCosts: round2(extraCosts),
    totalCost: round2(totalCost),
    markupPct,
    markupAmount: round2(markupAmount),
    basePrice: round2(basePrice),
    saleFeePct,
    finalPrice: round2(finalPrice),
    currency: DEFAULT_CURRENCY,
  };
}
